import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export interface PopoverPosition {
    top: number;
    left: number;
}

const GAP = 6;
const VIEWPORT_PADDING = 8;

/*
 * Попап рендерится порталом в body и позиционируется fixed по координатам
 * триггера. Так он не обрезается overflow/transform у родителей — например,
 * телом модалки — и всегда оказывается поверх неё.
 */
export const usePopover = <T extends HTMLElement>() => {
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState<PopoverPosition>({ top: 0, left: 0 });

    const rootRef = useRef<T>(null);
    const popoverRef = useRef<HTMLDivElement>(null);

    const updatePosition = useCallback(() => {
        const anchor = rootRef.current?.getBoundingClientRect();
        const popover = popoverRef.current?.getBoundingClientRect();
        if (!anchor || !popover) return;

        const fitsBelow = anchor.bottom + GAP + popover.height <= window.innerHeight - VIEWPORT_PADDING;
        const top = fitsBelow || anchor.top - GAP - popover.height < VIEWPORT_PADDING
            ? anchor.bottom + GAP
            : anchor.top - GAP - popover.height;

        const left = Math.max(
            VIEWPORT_PADDING,
            Math.min(anchor.left, window.innerWidth - VIEWPORT_PADDING - popover.width),
        );

        setPosition((prev) => (prev.top === top && prev.left === left ? prev : { top, left }));
    }, []);

    useLayoutEffect(() => {
        if (!isOpen) return undefined;

        updatePosition();

        /* capture: ловим скролл любого контейнера, в том числе тела модалки. */
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);

        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [isOpen, updatePosition]);

    useEffect(() => {
        if (!isOpen) return undefined;

        const handlePointerDown = (event: MouseEvent) => {
            const target = event.target as Node;
            const insideTrigger = rootRef.current?.contains(target);
            const insidePopover = popoverRef.current?.contains(target);

            if (!insideTrigger && !insidePopover) setIsOpen(false);
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                /* Иначе Escape дойдёт до модалки и закроет её вместе с попапом. */
                event.stopPropagation();
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown, true);

        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [isOpen]);

    return { isOpen, setIsOpen, rootRef, popoverRef, position };
};